import { useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

export default function ImageUpload({ value, onChange, label = 'Cover Image', hint = '' }) {
  const { user } = useAuth()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  async function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return

    // Validate
    if (!file.type.startsWith('image/')) { setError('Only image files allowed.'); return }
    if (file.size > 5 * 1024 * 1024) { setError('Max file size is 5MB.'); return }
    setError(null)
    setUploading(true)

    const ext = file.name.split('.').pop()
    const path = `${user.id}/${Date.now()}.${ext}`

    const { data, error: uploadError } = await supabase.storage
      .from('post-images')
      .upload(path, file, { upsert: true })

    if (uploadError) {
      setError(uploadError.message)
      setUploading(false)
      return
    }

    const { data: { publicUrl } } = supabase.storage
      .from('post-images')
      .getPublicUrl(path)

    onChange(publicUrl)
    setUploading(false)
  }

  async function remove() {
    onChange(null)
  }

  return (
    <div style={styles.wrap}>
      <label style={styles.label}>
        {label}
        {hint && <span style={styles.hint}>{hint}</span>}
      </label>

      {value ? (
        <div style={styles.preview}>
          <img src={value} alt="Upload preview" style={styles.previewImg} />
          <button type="button" onClick={remove} style={styles.removeBtn} title="Remove image">✕</button>
        </div>
      ) : (
        <div
          style={{ ...styles.dropzone, ...(uploading ? styles.dropzoneUploading : {}) }}
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault()
            const file = e.dataTransfer.files[0]
            if (file) { inputRef.current.files = e.dataTransfer.files; handleFile({ target: { files: [file] } }) }
          }}
        >
          {uploading ? (
            <p style={styles.dropzoneText}>Uploading…</p>
          ) : (
            <>
              <p style={styles.dropzoneIcon}>⊕</p>
              <p style={styles.dropzoneText}>Click or drag to upload</p>
              <p style={styles.dropzoneHint}>JPG, PNG, WebP — max 5MB</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
        </div>
      )}

      {error && <p style={styles.error}>{error}</p>}
    </div>
  )
}

const styles = {
  wrap: { marginBottom: '1.25rem' },
  label: {
    display: 'block', fontFamily: "'DM Mono', monospace",
    fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'var(--muted)', marginBottom: '0.6rem',
  },
  hint: { marginLeft: '0.75rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '0.6rem' },
  dropzone: {
    border: '1px dashed var(--rule)', padding: '2rem',
    textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s',
    background: 'var(--cream)',
  },
  dropzoneUploading: { opacity: 0.6, cursor: 'default' },
  dropzoneIcon: { fontSize: '1.5rem', color: 'var(--accent)', marginBottom: '0.5rem' },
  dropzoneText: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.06em' },
  dropzoneHint: { fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--rule)', marginTop: '0.3rem' },
  preview: { position: 'relative', display: 'inline-block' },
  previewImg: { width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block', border: '1px solid var(--rule)' },
  removeBtn: {
    position: 'absolute', top: 8, right: 8,
    background: 'rgba(15,14,12,0.7)', border: 'none',
    color: '#fff', width: 28, height: 28, fontSize: '0.7rem',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  error: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', color: 'var(--accent)', marginTop: '0.4rem' },
}
